import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../../GenericFormPopup/GenericFormPopup";
import {
  harshEventFieldConfig,
  harshEventInitialValues,
} from "../../../../formConfigs/Fleet/harshEventFieldConfig";
import {
  fetchAllDrivers,
  fetchAssets,
} from "../../IncidentPopup/IncidentPopup";
import { getId } from "../../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const HarshEventPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open && !!token,
  });

  const { data: trucks } = useQuery({
    queryKey: ["assets", "Truck"],
    queryFn: () => fetchAssets("Truck", token),
    enabled: open && !!token,
  });

  const handleOpen = () => {
    setStatus("idle");
    setErrorMessage(null);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/harshEvent`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setErrorMessage(null);
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["harshEvents"] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit harsh event");
    },
  });

  const submitHandler = (data: any) => {
    const { driverId, truckId, ...rest } = data;
    const finalData = {
      ...rest,
      driverId: getId(driverId),
      truckId: getId(truckId),
    };
    mutation.mutate(finalData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add Harsh Event
      </Button>
      <GenericFormPopup
        formFields={harshEventFieldConfig}
        initialValues={harshEventInitialValues}
        title="Add Harsh Event"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        autocompleteData={{
          drivers: allDrivers,
          trucks: trucks,
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage={"Harsh event added successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default HarshEventPopup;
